
import React from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAdminSettings } from "@/hooks/useAdminSettings";
import { GeneralSettingsTab } from "@/components/admin/settings/GeneralSettingsTab";
import { WhatsAppTab } from "@/components/admin/settings/WhatsAppTab";
import { CalendarDefaultsTab } from "@/components/admin/settings/CalendarDefaultsTab";
import { SmsSettingsTab } from "@/components/admin/settings/SmsSettingsTab";
import { ConsoleLogsTab } from "@/components/admin/settings/ConsoleLogsTab";
import { DisplayTab } from "@/components/admin/settings/DisplayTab";
import { PWATab } from "@/components/admin/settings/PWATab";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle, MessageSquare, Terminal, Monitor, Smartphone } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const AdminSettings = () => {
  const { settings, isSaving, isLoading, saveSettings, updateSettings } = useAdminSettings();
  const [activeTab, setActiveTab] = React.useState("notifications");
  const [edgeFunctionError, setEdgeFunctionError] = React.useState<string | null>(null);

  React.useEffect(() => {
    // Clear edge function error when tab changes
    setEdgeFunctionError(null);
  }, [activeTab]);

  if (isLoading) {
    return (
      <AdminLayout title="Admin Settings">
        <div className="space-y-4 w-full">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-[450px] w-full" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Admin Settings">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Admin Settings</h1>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="calendar">Calendar Defaults</TabsTrigger>
          <TabsTrigger value="display">
            <div className="flex items-center gap-1">
              <Monitor className="h-4 w-4" />
              <span>Display</span>
            </div>
          </TabsTrigger>
          <TabsTrigger value="whatsapp">WhatsApp</TabsTrigger>
          <TabsTrigger value="sms">
            <div className="flex items-center gap-1">
              <MessageSquare className="h-4 w-4" />
              <span>SMS</span>
            </div>
          </TabsTrigger>
          <TabsTrigger value="console-logs">
            <div className="flex items-center gap-1">
              <Terminal className="h-4 w-4" />
              <span>Console Logs</span>
            </div>
          </TabsTrigger>
          <TabsTrigger value="pwa">
            <div className="flex items-center gap-1">
              <Smartphone className="h-4 w-4" />
              <span>PWA</span>
            </div>
          </TabsTrigger>
        </TabsList>
        
        {edgeFunctionError && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Edge Function Error</AlertTitle>
            <AlertDescription>
              {edgeFunctionError}
            </AlertDescription>
          </Alert>
        )}
        
        <TabsContent value="notifications">
          <GeneralSettingsTab 
            settings={settings}
            updateSettings={updateSettings}
            saveSettings={saveSettings}
            isSaving={isSaving}
          />
        </TabsContent>
        
        <TabsContent value="calendar">
          <CalendarDefaultsTab 
            settings={settings}
            updateSettings={updateSettings}
            saveSettings={saveSettings}
            isSaving={isSaving}
          />
        </TabsContent>
        
        <TabsContent value="display">
          <DisplayTab 
            settings={settings}
            updateSettings={updateSettings}
            saveSettings={saveSettings}
            isSaving={isSaving}
          />
        </TabsContent>
        
        <TabsContent value="whatsapp">
          <WhatsAppTab 
            settings={settings}
            updateSettings={updateSettings}
            saveSettings={saveSettings}
            isSaving={isSaving}
            setEdgeFunctionError={setEdgeFunctionError}
          />
        </TabsContent>
        
        <TabsContent value="sms">
          <SmsSettingsTab 
            settings={settings}
            updateSettings={updateSettings}
            saveSettings={saveSettings}
            isSaving={isSaving}
          />
        </TabsContent>
        
        <TabsContent value="console-logs">
          <ConsoleLogsTab 
            settings={settings}
            updateSettings={updateSettings}
            saveSettings={saveSettings}
            isSaving={isSaving}
          />
        </TabsContent>
        
        <TabsContent value="pwa">
          <PWATab 
            settings={settings}
            updateSettings={updateSettings}
            saveSettings={saveSettings}
            isSaving={isSaving}
          />
        </TabsContent>
      </Tabs>
    </AdminLayout>
  );
};

export default AdminSettings;
